import { InfiniteTable } from '@/components/_common';
import DeleteIcon from '@/components/_common/DeleteIcon';
import EditIcon from '@/components/_common/EditIcon';
import { usePaymentsConditionReduced } from '@/core/domain/enterprise/PaymentCondition/PaymentCondition.hooks';
import {
  ListPaymentConditionsQuery,
  PaymentCondition,
  PaymentConditionReduced,
} from '@/core/domain/enterprise/PaymentCondition/PaymentCondition.types';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { quantityFormatter } from '@/utils/number';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { Divider, Space } from 'antd';
import { ColumnsType } from 'antd/lib/table';

import { useIntl } from 'react-intl';

type Props = {
  update: (paymentId: number) => void;
  remove: (payment: PaymentCondition) => void;
  params?: ListPaymentConditionsQuery;
};

const PaymentsConditionList: React.FC<Props> = ({ update, remove, params }) => {
  const intl = useIntl();

  const paymentsCondition = usePaymentsConditionReduced(params);
  const paymentsConditionData = flatInfiniteData(paymentsCondition.data);

  const columns: ColumnsType<PaymentConditionReduced> = [
    {
      dataIndex: 'name',
      title: intl.formatMessage({ id: 'paymentConditions.domain.name' }),
      render: (value) => value || '-',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
    {
      dataIndex: 'percentage',
      title: intl.formatMessage({ id: 'paymentConditions.domain.percentage' }),
      render: (value) => quantityFormatter(value) || '-',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
    {
      dataIndex: 'paymentQuantity',
      title: intl.formatMessage({
        id: 'paymentConditions.domain.installments',
      }),
      render: (value) => value || '-',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
    {
      title: intl.formatMessage({
        id: 'commons.list.header.action',
      }),
      width: 100,
      fixed: 'right',
      align: 'right',
      render: (record) => (
        <Space>
          <EditIcon
            iconProps={{ onClick: () => update(record.id) }}
            disabled={
              !hasPermissions([Authorities.REGISTER_PAYMENT_CONDITIONS_EDIT])
            }
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => remove(record)}
            disabled={
              !hasPermissions([Authorities.REGISTER_PAYMENT_CONDITIONS_DELETE])
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <InfiniteTable
      size="middle"
      columns={columns}
      dataSource={paymentsConditionData}
      loading={paymentsCondition.isFetching}
      rowKey={(row) => row.id}
      hasNextPage={paymentsCondition.hasNextPage}
      fetchNextPage={paymentsCondition.fetchNextPage}
      pagination={false}
      scroll={{ x: getTableScrollX(columns) }}
    />
  );
};

export default PaymentsConditionList;
